import { ArrowBack } from "@mui/icons-material";
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
} from "@mui/material";
import { ReactNode, Ref } from "react";
import { GetPortraitResponse } from "tng-address-collection-shared/types/portraitsApiTypes.ts";
import { usePortraitsTranslator } from "../i18n/translator";
import { PortraitFlow } from "./usePortraitFlow";

export type StepProps = {
  portrait: GetPortraitResponse;
  name: string | undefined;
  flow: PortraitFlow;
  headingRef: Ref<HTMLHeadingElement>;
};

export const FlowPanel = ({
  name,
  flow,
  headingRef,
  stepTitle,
  hint,
  picture,
  status,
  error,
  notice,
  actions,
}: Omit<StepProps, "portrait"> & {
  stepTitle: string;
  hint: string;
  picture: ReactNode;
  status: string | undefined;
  error: string | undefined;
  notice?: ReactNode;
  actions: ReactNode;
}) => {
  const translator = usePortraitsTranslator();

  return (
    <Card variant="outlined" sx={{ width: "100%", maxWidth: 480 }}>
      <CardHeader
        disableTypography
        title={
          name && (
            <Typography variant="h5" component="h2">
              {name}
            </Typography>
          )
        }
        subheader={
          <Typography
            ref={headingRef}
            variant="subtitle1"
            component="h3"
            tabIndex={-1}
            sx={{ outline: "none" }}
          >
            {stepTitle}
          </Typography>
        }
      />
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {hint}
          </Typography>
          <Stack spacing={1} alignItems="center">
            {picture}
            <Typography role="status" variant="body2" textAlign="center">
              {status}
            </Typography>
          </Stack>
          {error && <Alert severity="error">{error}</Alert>}
          {notice}
          <Stack spacing={1}>{actions}</Stack>
          {flow.canGoBackToCard && (
            <Button
              startIcon={<ArrowBack />}
              disabled={flow.busy}
              onClick={() => flow.open("card")}
              sx={{ alignSelf: "center" }}
            >
              {translator("backToCard")}
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
